import React, { useState, useMemo } from 'react';
import { BadgeCheck, MapPin, Store, ExternalLink, RefreshCw, Clock, Star, MessageCircle } from 'lucide-react';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import { isSpamContent, isSpamAccount } from '../lib/bchFilter';
import PostCard from '../components/PostCard';

// Hashtag used to collect all BCH merchant visit posts
const VERIFIED_TAG = 'bchverified';

export default function VerifiedPage() {
  const { isMuted } = useAppContext();
  const [sort, setSort] = useState<'recent' | 'top'>('recent');
  const [since] = useState(() => {
    const now = Math.floor(Date.now() / 1000);
    return Math.floor((now - 3600 * 24 * 90) / 3600) * 3600; // last 90 days
  });

  const { events, loading, refetch } = useNostrQuery({
    filters: [
      { kinds: [1], '#t': [VERIFIED_TAG], limit: 100, since },
    ],
  });

  const posts = useMemo(() => {
    const seen = new Set<string>();
    const filtered = events.filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      if (isMuted(e.pubkey)) return false;
      if (isSpamContent(e.content)) return false;
      if (isSpamAccount(e.pubkey)) return false;
      return true;
    });

    if (sort === 'top') {
      return filtered.sort((a, b) => {
        const aHasImage = a.content.includes('http') && (a.content.includes('.jpg') || a.content.includes('.png') || a.content.includes('.webp') || a.tags.some((t: string[]) => t[0] === 'imeta'));
        const bHasImage = b.content.includes('http') && (b.content.includes('.jpg') || b.content.includes('.png') || b.content.includes('.webp') || b.tags.some((t: string[]) => t[0] === 'imeta'));
        if (aHasImage && !bHasImage) return -1;
        if (!aHasImage && bHasImage) return 1;
        return b.created_at - a.created_at;
      });
    }
    return filtered.sort((a, b) => b.created_at - a.created_at);
  }, [events, sort, isMuted]);

  const uniquePosters = useMemo(() => new Set(posts.map(p => p.pubkey)).size, [posts]);

  return (
    <div>
      {/* ── Header ── */}
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-sm shadow-emerald-500/20">
              <BadgeCheck className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">BCH Verified</h1>
              <p className="text-[10px] text-muted-foreground -mt-0.5">Merchant visit buzz</p>
            </div>
          </div>
          <button onClick={refetch} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── BCH Local CTA ── */}
      <div className="px-4 pt-4 pb-2">
        <a
          href="https://bchlocal.com"
          target="_blank"
          rel="noopener noreferrer"
          className="block glass-card bg-gradient-to-br from-emerald-500/5 to-green-500/5 border-emerald-500/15 hover:border-emerald-500/30 hover:shadow-md hover:shadow-emerald-500/5 transition-all group"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/20 group-hover:scale-105 transition-transform">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm">BCH Local</h3>
                <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-emerald-500 transition-colors" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                Find and verify BCH-accepting businesses near you. Add new merchants, leave reviews, and help map real Bitcoin Cash adoption around the world.
              </p>
              <div className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold border border-emerald-500/20 group-hover:bg-emerald-500/15 transition-colors">
                <Store className="w-3.5 h-3.5" /> Browse Merchants on BCH Local
              </div>
            </div>
          </div>
        </a>
      </div>

      {/* ── Social buzz explainer ── */}
      <div className="px-4 pb-2">
        <div className="glass-card py-3">
          <div className="flex items-center gap-2.5">
            <MessageCircle className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground leading-relaxed">
                See what the community is saying about BCH merchants. Posts tagged <span className="font-semibold text-emerald-500">#bchverified</span> from across Nostr show up here.
              </p>
            </div>
          </div>
          {(posts.length > 0 || loading) && (
            <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-border/60">
              <div className="text-center flex-1">
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{posts.length}</p>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Posts</p>
              </div>
              <div className="w-px h-6 bg-border" />
              <div className="text-center flex-1">
                <p className="text-sm font-bold">{uniquePosters}</p>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wide">People</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Sort tabs ── */}
      {posts.length > 0 && (
        <div className="px-4 pt-1 pb-1 flex items-center gap-2">
          <button
            onClick={() => setSort('recent')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              sort === 'recent' ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            <Clock className="w-3 h-3" /> Recent
          </button>
          <button
            onClick={() => setSort('top')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              sort === 'top' ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            <Star className="w-3 h-3" /> Photos First
          </button>
        </div>
      )}

      {/* ── Feed ── */}
      <div>
        {loading && posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <BadgeCheck className="w-6 h-6 text-emerald-500/50" />
            </div>
            <div className="w-6 h-6 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading posts…</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500/10 to-green-500/10 flex items-center justify-center mb-3 border border-emerald-500/10">
              <BadgeCheck className="w-7 h-7 text-emerald-400/60" />
            </div>
            <p className="font-medium text-muted-foreground">No posts yet</p>
            <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs mx-auto">
              Visit a BCH merchant and tag your post with <span className="font-semibold text-emerald-500">#bchverified</span> to share your experience here.
            </p>
            <a
              href="https://bchlocal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-semibold hover:bg-emerald-500/20 transition-colors border border-emerald-500/20"
            >
              <MapPin className="w-4 h-4" /> Find Merchants on BCH Local
            </a>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {posts.map(post => (
              <PostCard key={post.id} event={post} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

